import React, { useState, useContext, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { UserContext } from '../contexts/UserContext';
import { NavigationContext } from '../contexts/NavigationContext';
import useLocale from '../hooks/useLocale';
import './UserHeader.css';

const UserHeader = () => {
  const [menuVisible, setMenuVisible] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const langRef = useRef(null);

  const { user, logout } = useContext(UserContext);
  const navigation = useContext(NavigationContext);
  const { getCurrentLanguage, setLanguage } = useLocale();
  const currentLang = getCurrentLanguage();
  const breadcrumbs = navigation?.breadcrumbs || [];
  const navigate = useNavigate();
  const ucpBaseUrlRaw =
    (typeof process !== 'undefined' && process?.env?.REACT_APP_UCP_BASE_URL) ||
    'https://ucp.academy';

  const ucpHomeUrl = String(ucpBaseUrlRaw || '').replace(/\/$/, '');

  useEffect(() => {
    if (!langMenuOpen) return;

    const handleClickOutside = (event) => {
      if (langRef.current && !langRef.current.contains(event.target)) {
        setLangMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [langMenuOpen]);

  useEffect(() => {
    const ucpSpan = document.querySelector('.header.user-header .logo span:first-child');
    if (!ucpSpan) return;

    const onClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      window.location.href = ucpHomeUrl;
    };

    const onKeyDown = (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
        window.location.href = ucpHomeUrl;
      }
    };

    ucpSpan.addEventListener('click', onClick);
    ucpSpan.addEventListener('keydown', onKeyDown);

    // Не меняем внешний вид — только делаем элемент интерактивным
    ucpSpan.setAttribute('role', 'link');
    ucpSpan.setAttribute('tabindex', '0');
    ucpSpan.setAttribute('aria-label', 'UCP');
    ucpSpan.style.cursor = 'pointer';

    return () => {
      ucpSpan.removeEventListener('click', onClick);
      ucpSpan.removeEventListener('keydown', onKeyDown);
    };
  }, [ucpHomeUrl]);

  const userAvatar = user && user.photo ? user.photo : '/user_icon.png';
  const userName = user && user.full_name ? user.full_name : user?.email || '';

  const translations =
    currentLang === 'ru'
      ? {
          logout: 'Выйти',
          myFests: 'Мои сборники',
          backToUcp: 'Перейти в UCP',
          notifications: 'Email-уведомления',
        }
      : {
          logout: 'Logout',
          myFests: 'My Festschrifts',
          backToUcp: 'Back to UCP',
          notifications: 'Email notifications',
        };

  const toggleMenu = () => {
    setMenuVisible((prev) => !prev);
  };

  const toggleLangMenu = (event) => {
    event.stopPropagation();
    setLangMenuOpen((prev) => !prev);
  };

  const handleLogout = async (event) => {
    event.stopPropagation();
    await logout();
    navigate('/login');
  };

  const handleGoToUcp = (event) => {
    event.stopPropagation();
    window.location.href = ucpHomeUrl;
  };

  const handleOpenNotifications = (event) => {
    event.stopPropagation();
    setMenuVisible(false);
    navigate('/settings/notifications');
  };

  const handleLangChange = (code) => {
    setLanguage(code);
    setLangMenuOpen(false);
  };

  return (
    <header className="header user-header">
      {/* Верхняя полоса: логотип, язык, юзер */}
      <div className="header__top">
        <div className="header__container">
          {/* Лого ucp */}
          <Link to="/dashboard" className="logo">
            <span>ucp</span>
            <span>Review</span>
          </Link>

          <div className="header__action">
            {/* Фейковый переключатель языка RU / EN */}
            <div className={`lang ${langMenuOpen ? 'lang--open' : ''}`} ref={langRef}>
              <button className="lang__btn" type="button" onClick={toggleLangMenu}>
                {currentLang.toUpperCase()}
              </button>
              <ul className="lang__submenu submenu">
                {['ru', 'en']
                  .filter((code) => code !== currentLang)
                  .map((code) => (
                    <li key={code} className="submenu__item">
                      <button
                        type="button"
                        className="submenu__link"
                        onClick={() => handleLangChange(code)}
                      >
                        {code.toUpperCase()}
                      </button>
                    </li>
                  ))}
              </ul>
            </div>

            {/* Блок пользователя с дропдауном */}
            <div className="header__btns">
              <div
                className={
                  'header__link header__link_user' + (menuVisible ? ' header__link_user_open' : '')
                }
                onClick={toggleMenu}
              >
                <img src={userAvatar} alt="User Avatar" className="img-user" />
                <span className="header__user-name">{userName}</span>

                <div className="submenu-user">
                  <div className="submenu-user__header">
                    <strong className="submenu-user__email">{userName}</strong>
                  </div>
                  <div className="submenu-user__body">
                    <ul className="submenu-user__list">
                      <li className="submenu-user__item">
                        <button
                          type="button"
                          className="submenu-user__link"
                          onClick={handleOpenNotifications}
                        >
                          {translations.notifications}
                        </button>
                      </li>

                      <li className="submenu-user__item">
                        <button
                          type="button"
                          className="submenu-user__link"
                          onClick={handleGoToUcp}
                        >
                          {translations.backToUcp}
                        </button>
                      </li>

                      <li className="submenu-user__item">
                        <button type="button" className="submenu-user__link" onClick={handleLogout}>
                          {translations.logout}
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Нижняя полоса: хлебные крошки как раньше */}
      <div className="header__bottom">
        <div className="header__container">
          <nav className="breadcrumb">
            <Link to="/dashboard" className="breadcrumb-link">
              {translations.myFests}
            </Link>
            {breadcrumbs.map((item, index) => (
              <React.Fragment key={index}>
                <span className="breadcrumb-separator"> / </span>
                {item.url ? (
                  <Link to={item.url} className="breadcrumb-link">
                    {item.label}
                  </Link>
                ) : (
                  <span className="breadcrumb-current">{item.label}</span>
                )}
              </React.Fragment>
            ))}
          </nav>
        </div>
      </div>
    </header>
  );
};

export default UserHeader;
